import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  NormalizedCacheObject,
  Operation,
} from "@apollo/client";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { InMemoryCache } from "@apollo/client";
import {
  getCurrentToken,
  getKcClientId,
  getToken,
  isNetworkDisconnectError,
  NETWORK_DISCONNECT_EVENT,
} from "@onmo/auth";
import { GraphQLError } from "graphql";
import { getProfileToken } from "./profileSession";
import { createInitAwareWebSocket } from "./initAwareWebSocket";

let wsLink: WebSocketLink | null = null;
let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let subscriptionClient: SubscriptionClient | null = null;

export const API_CFG = JSON.parse(
  import.meta.env.VITE_API_CFG || import.meta.env.REACT_APP_ONMO_CFG || "{}",
);
export enum ApiLegacyName {
  Legacy = "legacy",
  PrimaryLegacy = "primaryLegacy",
}

export interface ApolloClientOptions {
  onGraphQLError?: (error: GraphQLError, operation: Operation) => void;
  onCrossTenantAuthFailed?: () => void;
  onProfileTokenInvalid?: () => void;
}

export const CrossTenantAuthorizationError =
  "CROSS_TENANT_AUTHORIZATION_FAILED";

export const ProfileTokenInvalidError = "PROFILE_TOKEN_INVALID";

const hasErrorCode = (
  code: string,
  errors?: ReadonlyArray<{ extensions?: unknown }>,
) =>
  (errors || []).some(
    (e) => (e?.extensions as { code?: string } | undefined)?.code === code,
  );

const isCrossTenantError = (errors?: ReadonlyArray<{ extensions?: unknown }>) =>
  hasErrorCode(CrossTenantAuthorizationError, errors);
export const cache = new InMemoryCache({
  typePolicies: {
    StreamProfile: { keyFields: false },
    Config: { merge: true },
    User: {
      fields: {
        metadata: {
          merge: true,
        },
        deviceStreamConfig: {
          keyArgs: ["deviceInfo"],
          merge: true,
        },
        gameLibrary: {
          keyArgs: ["input", ["filter", "gameCategory"]],
          merge(existing = { items: [] }, incoming, { args }) {
            const offset = args?.input?.page?.offset ?? 0;
            if (offset === 0) return incoming;

            const items = [...existing.items];
            incoming.items.forEach((item, index) => {
              items[offset + index] = item;
            });
            return { ...incoming, items };
          },
        },
        recentlyPlayedGames: {
          keyArgs: ["input"],
          merge(_existing, incoming) {
            return incoming;
          },
        },
        inventories: {
          keyArgs: ["input", ["filters"]],
          merge(existing = { items: [], total: 0 }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...existing.items, ...incoming.items],
            };
          },
        },
        activities: {
          keyArgs: ["input", ["category"]],
          merge(existing = { items: [] }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...existing.items, ...incoming.items],
            };
          },
        },
        following: {
          keyArgs: false,
          merge(existing = { items: [], total: 0 }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...(existing.items || []), ...incoming.items],
            };
          },
        },
        followers: {
          keyArgs: false,
          merge(existing = { items: [], total: 0 }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...(existing.items || []), ...incoming.items],
            };
          },
        },
      },
    },
    Game: {
      fields: {
        type: {
          merge(existing, incoming) {
            if (existing === "DIRECT_HTML" && incoming === "HTML") {
              return existing;
            }
            return incoming;
          },
        },
      },
    },
    Query: {
      fields: {
        game: {
          read(existing, { args, toReference }) {
            return (
              existing ?? toReference({ __typename: "Game", id: args?.id })
            );
          },
        },
        leaderboard: {
          keyArgs: ["input", ["globalLeaderboardType"]],
          merge(existing = { items: [] }, incoming) {
            return {
              ...incoming,
              items: [...existing.items, ...incoming.items],
            };
          },
        },
        featuredCards: {
          keyArgs: ["input", ["group"]],
          merge(existing = { items: [] }, incoming, { args }) {
            const offset = args?.input?.page?.offset ?? 0;
            if (offset === 0) return incoming;

            const items = [...existing.items];
            incoming.items.forEach((item: unknown, index: number) => {
              items[offset + index] = item;
            });
            return { ...incoming, items };
          },
        },
        me: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
  },
});

export const getSubscriptionClient = () => {
  return subscriptionClient;
};

//create Subscription Client with correct token
const createSubscriptionClient = () => {
  const token = getCurrentToken();
  if (!token) return null;
  if (!wsLink) {
    subscriptionClient = new SubscriptionClient(
      API_CFG?.api?.wsApolloUrl || "",
      {
        minTimeout: 10000,
        reconnect: true,
        connectionParams: async () => {
          const accessToken = await getToken();
          return {
            headers: { authorization: accessToken },
          };
        },
        connectionCallback(error, result?) {
          console.log("[subscription] - Connection callback", {
            result,
            error,
          });
        },
      },
      createInitAwareWebSocket(),
      [],
    );
    wsLink = new WebSocketLink(subscriptionClient);
  }

  return wsLink;
};

export const createApolloClient = (options?: ApolloClientOptions) => {
  const httpLink = createHttpLink({
    uri: API_CFG?.api?.httpApolloUrl,
    includeExtensions: true,
  });
  const legacyHttpLink = createHttpLink({
    uri: API_CFG?.api?.legacyHttpApolloUrl,
    includeExtensions: true,
  });
  const primaryLegacyHttpLink = createHttpLink({
    uri: API_CFG?.api?.primaryLegacyHttpApolloUrl,
    includeExtensions: true,
  });
  const isLegacyApi = (api: string) => {
    return (Object.values(ApiLegacyName) as string[]).includes(api);
  };

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // Signal app via window event
    if (isNetworkDisconnectError(networkError)) {
      window.dispatchEvent(new CustomEvent(NETWORK_DISCONNECT_EVENT));
      return;
    }

    const serverErrors = (networkError as { result?: { errors?: unknown } })
      ?.result?.errors as ReadonlyArray<{ extensions?: unknown }> | undefined;
    if (isCrossTenantError(graphQLErrors) || isCrossTenantError(serverErrors)) {
      options?.onCrossTenantAuthFailed?.();
    }

    // A per-request PIN verification token expiring is not the session expiring
    const hasOwnProfileToken = !!operation.getContext().profileToken;
    if (
      !hasOwnProfileToken &&
      hasErrorCode(ProfileTokenInvalidError, graphQLErrors)
    ) {
      options?.onProfileTokenInvalid?.();
    }

    graphQLErrors?.forEach((err) => options?.onGraphQLError?.(err, operation));
  });

  const authLink = setContext(async (_, context) => {
    const { headers, api, skipProfileToken } = context;
    const accessToken = await getToken();
    if (!accessToken) {
      const clientId = getKcClientId();
      return {
        headers: {
          ...headers,
          "x-client-id": clientId,
        },
      };
    } else {
      const profileToken = context.profileToken ?? getProfileToken();
      const profileHeaders =
        profileToken && !skipProfileToken && !isLegacyApi(api)
          ? { "x-profile-token": profileToken }
          : {};
      return {
        headers: {
          ...headers,
          ...profileHeaders,
          authorization: !isLegacyApi(api)
            ? `Bearer ${accessToken}`
            : accessToken,
        },
      };
    }
  });
  const mergedHttpLink = ApolloLink.split(
    (operation) => !isLegacyApi(operation.getContext().api),
    ApolloLink.from([errorLink, authLink, httpLink]),
    ApolloLink.split(
      (operation) => operation.getContext().api === ApiLegacyName.Legacy,
      ApolloLink.from([errorLink, authLink, legacyHttpLink]),
      ApolloLink.from([errorLink, authLink, primaryLegacyHttpLink]),
    ),
  );

  wsLink = createSubscriptionClient();

  const link = wsLink
    ? ApolloLink.split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          );
        },
        wsLink, //use WebSocket for subscription
        mergedHttpLink,
      )
    : mergedHttpLink;

  apolloClient = new ApolloClient({
    queryDeduplication: true,
    link,
    cache: cache,
  });

  return apolloClient;
};

export const client = createApolloClient();
